import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../api';
import { BarChart3, FileText, Users, TrendingUp, CheckCircle, UserCheck, Briefcase, ChevronDown, ChevronUp, Edit2, Trash2, Eye, Mail, ExternalLink, Search, StickyNote, Calendar, Copy, Power, PowerOff, GitCompare, Download } from 'lucide-react';
import UserManagement from '../components/UserManagement';
import InterviewScheduler, { InterviewsList } from '../components/InterviewScheduler';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import NotesModal from '../components/NotesModal';
import TagSelector from '../components/TagSelector';
import RatingStars from '../components/RatingStars';
import WorkflowIndicator from '../components/WorkflowIndicator';
import BulkActionsBar from '../components/BulkActionsBar';
import CandidateComparisonModal from '../components/CandidateComparisonModal';
import PipelineBoard from '../components/PipelineBoard';

export default function Dashboard() {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userJobs, setUserJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [expandedJob, setExpandedJob] = useState(null);
  const [jobApplications, setJobApplications] = useState({});
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showInterviewScheduler, setShowInterviewScheduler] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(null);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [viewMode, setViewMode] = useState('table');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchDashboardData = async () => {
      try {
        const statsResponse = await api.get(`/api/users/dashboard/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(statsResponse.data);

        if (user.user_type === 'job_seeker') {
          const appsResponse = await api.get(`/api/applications/user/${user.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setApplications(appsResponse.data);
        } else if (user.user_type === 'employer') {
          const jobsResponse = await api.get(`/api/jobs?employer_id=${user.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const jobs = jobsResponse.data;
          setUserJobs(jobs);
          
          // Fetch applications for each job
          const appsMap = {};
          for (const job of jobs) {
            try {
              const appsRes = await api.get(`/api/applications/job/${job.id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              appsMap[job.id] = appsRes.data || [];
            } catch (err) {
              console.error(`Failed to fetch data for job ${job.id}:`, err);
              appsMap[job.id] = [];
            }
          }
          setJobApplications(appsMap);
        }
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user, token, navigate]);

  const handleUpdateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await api.put(`/api/applications/${applicationId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh applications for the expanded job
      if (expandedJob) {
        const appsRes = await api.get(`/api/applications/job/${expandedJob}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setJobApplications(prev => ({ ...prev, [expandedJob]: appsRes.data }));
      }
    } catch (err) {
      console.error('Failed to update application:', err);
      alert('Failed to update application status');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job posting? This will also delete all applications.')) {
      return;
    }
    
    try {
      await api.delete(`/api/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserJobs(prev => prev.filter(j => j.id !== jobId));
      alert('Job deleted successfully');
    } catch (err) {
      console.error('Failed to delete job:', err);
      alert('Failed to delete job');
    }
  };

  const handleDuplicateJob = async (jobId) => {
    try {
      const response = await api.post(`/api/jobs/${jobId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserJobs(prev => [response.data, ...prev]);
      alert('Job duplicated successfully');
    } catch (err) {
      console.error('Failed to duplicate job:', err);
      alert('Failed to duplicate job');
    }
  };

  const handleToggleJobStatus = async (jobId, currentStatus) => {
    try {
      const response = await api.put(`/api/jobs/${jobId}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserJobs(prev => prev.map(j => j.id === jobId ? response.data : j));
    } catch (err) {
      console.error('Failed to toggle job status:', err);
      alert('Failed to update job status');
    }
  };

  const toggleJobExpansion = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  const handleExportApplications = async () => {
    try {
      const response = await fetch(`https://tradelink-kseg.onrender.com/api/exports/applications?format=json`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      // Convert to CSV and download
      const csv = convertToCSV(data.applications);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `applications_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export applications');
    }
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(',')];
    
    for (const row of data) {
      const values = headers.map(header => {
        const val = row[header];
        return val === null || val === undefined ? '' : `"${String(val).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    }
    
    return csvRows.join('\n');
  };

  const refreshApplications = async () => {
    try {
      if (user?.user_type === 'job_seeker') {
        const appsResponse = await api.get(`/api/applications/user/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setApplications(appsResponse.data);
        return;
      }

      if (user?.user_type === 'employer') {
        if (expandedJob) {
          const appsRes = await api.get(`/api/applications/job/${expandedJob}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setJobApplications(prev => ({ ...prev, [expandedJob]: appsRes.data }));
          setSelectedApplications([]);
          return;
        }

        // Fallback: refresh all job applications to keep status in sync
        const appsMap = {};
        for (const job of userJobs) {
          try {
            const appsRes = await api.get(`/api/applications/job/${job.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            appsMap[job.id] = appsRes.data || [];
          } catch (err) {
            console.error(`Failed to fetch data for job ${job.id}:`, err);
            appsMap[job.id] = [];
          }
        }
        setJobApplications(appsMap);
        setSelectedApplications([]);
      }
    } catch (err) {
      console.error('Failed to refresh applications:', err);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><p>Loading...</p></div>;
  }

  // JOB SEEKER DASHBOARD
  if (user.user_type === 'job_seeker') {
    return (
      <div className="bg-gray-50 min-h-screen py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold mb-8">Job Seeker Dashboard</h1>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">Total Applications</p>
                  <p className="text-4xl font-bold">{stats?.totalApplications || 0}</p>
                </div>
                <FileText className="text-blue-600" size={40} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">Under Review</p>
                  <p className="text-4xl font-bold">{stats?.underReview || 0}</p>
                </div>
                <BarChart3 className="text-green-600" size={40} />
              </div>
            </div>
          </div>

          {/* Interviews Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">My Interviews</h2>
            <InterviewsList />
          </div>

          {/* Applications Table */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6">My Applications</h2>
            {applications.length === 0 ? (
              <p className="text-gray-500">You haven't applied for any jobs yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Job Title</th>
                      <th className="px-4 py-2 text-left">Company</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Applied</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr key={app.id} className="border-t hover:bg-gray-50">
                        <td className="px-4 py-3 font-semibold">{app.job_title}</td>
                        <td className="px-4 py-3">{app.employer_name}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              app.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              app.status === 'interview' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {app.status === 'pending' ? '⏳ Under Review' :
                               app.status === 'accepted' ? '✓ Accepted' :
                               app.status === 'interview' ? '📅 Interview Scheduled' :
                               '✗ Rejected'}
                            </span>
                            {app.status === 'accepted' && (
                              <span className="text-xs text-green-600 font-medium">→ Awaiting Interview</span>
                            )}
                            {app.status === 'interview' && (
                              <span className="text-xs text-blue-600 font-medium">→ Next Steps</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{new Date(app.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // EMPLOYER DASHBOARD
  if (user.user_type === 'employer') {
    const totalApplications = Object.values(jobApplications).reduce((sum, apps) => sum + apps.length, 0);
    
    return (
      <div className="bg-gray-50 min-h-screen py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-bold">Employer Dashboard</h1>
            <button 
              onClick={() => navigate('/post-job')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold"
            >
              + Post New Job
            </button>
          </div>

          {/* Analytics Dashboard */}
          <AnalyticsDashboard />

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">Total Jobs Posted</p>
                  <p className="text-4xl font-bold">{userJobs.length}</p>
                </div>
                <Briefcase className="text-blue-600" size={40} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">Total Applications</p>
                  <p className="text-4xl font-bold">{totalApplications}</p>
                </div>
                <Users className="text-green-600" size={40} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600">Pending Review</p>
                  <p className="text-4xl font-bold">
                    {Object.values(jobApplications).reduce((sum, apps) => 
                      sum + apps.filter(a => a.status === 'pending').length, 0
                    )}
                  </p>
                </div>
                <FileText className="text-yellow-600" size={40} />
              </div>
            </div>
          </div>

          {/* Interviews Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-6">Interview Schedule</h2>
            <InterviewsList />
          </div>

          {/* Job Postings with Applications */}
          <div className="bg-white rounded-lg shadow-lg p-4 sm:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <h2 className="text-xl sm:text-2xl font-bold">My Job Postings & Applications</h2>
              
              {/* Toolbar with Actions */}
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                {selectedApplications.length >= 2 && (
                  <button
                    onClick={() => setShowComparison(true)}
                    className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition text-sm font-medium"
                  >
                    <GitCompare size={16} />
                    <span className="hidden sm:inline">Compare</span> ({selectedApplications.length})
                  </button>
                )}
                <button
                  onClick={handleExportApplications}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition text-sm font-medium"
                >
                  <Download size={16} />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search applicants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            {userJobs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">You haven't posted any jobs yet.</p>
                <button 
                  onClick={() => navigate('/post-job')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                >
                  Post Your First Job
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {userJobs.map((job) => {
                  const apps = jobApplications[job.id] || [];
                  const normalizedSearch = searchTerm.trim().toLowerCase();
                  const filteredApps = apps.filter((app) => {
                    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
                    if (!normalizedSearch) return matchesStatus;
                    const haystack = `${app.name || ''} ${app.email || ''}`.toLowerCase();
                    return matchesStatus && haystack.includes(normalizedSearch);
                  });
                  const isExpanded = expandedJob === job.id;
                  
                  return (
                    <div key={job.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Job Header */}
                      <div className="p-4 bg-gray-50 flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg">{job.title}</h3>
                          <p className="text-gray-600 text-sm">{job.location} • {job.category}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {apps.length} application{apps.length !== 1 ? 's' : ''} • 
                            Posted {new Date(job.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Toggle Active/Inactive */}
                          <button
                            onClick={() => handleToggleJobStatus(job.id, job.is_active)}
                            className={`p-2 rounded ${job.is_active ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                            title={job.is_active ? 'Deactivate Job' : 'Activate Job'}
                          >
                            {job.is_active ? <Power size={20} /> : <PowerOff size={20} />}
                          </button>
                          
                          {/* Duplicate Job */}
                          <button
                            onClick={() => handleDuplicateJob(job.id)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                            title="Duplicate Job"
                          >
                            <Copy size={20} />
                          </button>
                          
                          <button
                            onClick={() => navigate(`/jobs/${job.id}`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="View Job"
                          >
                            <Eye size={20} />
                          </button>
                          <button
                            onClick={() => navigate(`/jobs/${job.id}/screening`)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                            title="Manage Screening Questions"
                          >
                            <StickyNote size={20} />
                          </button>
                          <button
                            onClick={() => navigate(`/post-job?edit=${job.id}`)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                            title="Edit Job"
                          >
                            <Edit2 size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteJob(job.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete Job"
                          >
                            <Trash2 size={20} />
                          </button>
                          <button
                            onClick={() => toggleJobExpansion(job.id)}
                            className="p-2 hover:bg-gray-100 rounded"
                          >
                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </button>
                        </div>
                      </div>

                      {/* Applications List (Expanded) */}
                      {isExpanded && (
                        <div className="p-4 border-t">
                          {apps.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">No applications yet</p>
                          ) : (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between flex-wrap gap-3">
                                <p className="text-sm text-gray-600">View applications as a table or pipeline.</p>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => setViewMode('table')}
                                    className={`px-3 py-1.5 rounded text-xs font-semibold border ${
                                      viewMode === 'table'
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                                  >
                                    Table
                                  </button>
                                  <button
                                    onClick={() => setViewMode('kanban')}
                                    className={`px-3 py-1.5 rounded text-xs font-semibold border ${
                                      viewMode === 'kanban'
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                                  >
                                    Pipeline
                                  </button>
                                </div>
                              </div>

                              {viewMode === 'table' ? (
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-2 py-2 text-left">
                                          <input
                                            type="checkbox"
                                            checked={filteredApps.length > 0 && filteredApps.every(app => selectedApplications.includes(app.id))}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setSelectedApplications(prev => [...new Set([...prev, ...filteredApps.map(app => app.id)])]);
                                              } else {
                                                setSelectedApplications(prev => prev.filter(id => !filteredApps.map(app => app.id).includes(id)));
                                              }
                                            }}
                                            className="w-4 h-4 cursor-pointer"
                                          />
                                        </th>
                                        <th className="px-4 py-2 text-left">Applicant</th>
                                        <th className="px-4 py-2 text-left hidden md:table-cell">Email</th>
                                        <th className="px-4 py-2 text-left hidden sm:table-cell">Applied</th>
                                        <th className="px-4 py-2 text-left">Status</th>
                                        <th className="px-4 py-2 text-left">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filteredApps.length === 0 ? (
                                        <tr>
                                          <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                                            No applications match your search or status filter.
                                          </td>
                                        </tr>
                                      ) : (
                                        filteredApps.map((app) => (
                                          <tr key={app.id} className="border-t hover:bg-gray-50">
                                          <td className="px-2 py-3">
                                            <input
                                              type="checkbox"
                                              checked={selectedApplications.includes(app.id)}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  setSelectedApplications(prev => [...prev, app.id]);
                                                } else {
                                                  setSelectedApplications(prev => prev.filter(id => id !== app.id));
                                                }
                                              }}
                                              className="w-4 h-4 cursor-pointer"
                                            />
                                          </td>
                                          <td className="px-4 py-3 font-medium">{app.name}</td>
                                          <td className="px-4 py-3 hidden md:table-cell">
                                            <a href={`mailto:${app.email}`} className="text-blue-600 hover:underline flex items-center gap-1">
                                              <Mail size={14} />
                                              {app.email}
                                            </a>
                                          </td>
                                          <td className="px-4 py-3 text-sm hidden sm:table-cell">{new Date(app.created_at).toLocaleDateString()}</td>
                                          <td className="px-4 py-3">
                                            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ${
                                              app.status === 'accepted' ? 'bg-green-100 text-green-800' :
                                              app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                              app.status === 'interview' ? 'bg-blue-100 text-blue-800' :
                                              app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                              'bg-gray-100 text-gray-800'
                                            }`}>
                                              {app.status === 'pending' ? '⏳ Pending' :
                                               app.status === 'accepted' ? '✓ Accepted' :
                                               app.status === 'interview' ? '📅 Interview' :
                                               app.status === 'rejected' ? '✗ Rejected' :
                                               app.status}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              {app.cv_url && (
                                                <a
                                                  href={app.cv_url.startsWith('http') ? app.cv_url : `${api.defaults.baseURL}${app.cv_url}`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200 font-semibold flex items-center gap-1 transition"
                                                  title="View CV in new tab"
                                                >
                                                  <ExternalLink size={14} />
                                                  <span className="hidden sm:inline">View CV</span>
                                                  <span className="sm:hidden">CV</span>
                                                </a>
                                              )}
                                              <button
                                                onClick={() => setSelectedApplicant(app)}
                                                className="px-2 sm:px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 font-semibold"
                                              >
                                                Review
                                              </button>
                                              {app.status === 'accepted' && (
                                                <button
                                                  onClick={() => {
                                                    setShowInterviewScheduler({
                                                      applicationId: app.id,
                                                      candidateName: app.name,
                                                      jobTitle: job.title
                                                    });
                                                  }}
                                                  className="px-2 sm:px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 font-semibold flex items-center gap-1"
                                                >
                                                  <Calendar size={14} />
                                                  <span className="hidden sm:inline">Schedule Interview</span>
                                                  <span className="sm:hidden">Interview</span>
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                          </tr>
                                        ))
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <PipelineBoard
                                  applications={filteredApps}
                                  onStatusChange={handleUpdateApplicationStatus}
                                  onReview={(app) => setSelectedApplicant(app)}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Applicant Details Modal */}
        {selectedApplicant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedApplicant(null)}>
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Application Review</h3>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        selectedApplicant.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        selectedApplicant.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {selectedApplicant.status === 'pending' ? '⏳ Pending Review' :
                         selectedApplicant.status === 'accepted' ? '✓ Accepted' :
                         '✗ Rejected'}
                      </span>
                      {selectedApplicant.status === 'accepted' && (
                        <span className="text-sm text-gray-600">→ Ready to schedule interview</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => setSelectedApplicant(null)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {/* Workflow Indicator */}
                <WorkflowIndicator currentStatus={selectedApplicant.status} />

                <div>
                  <label className="font-semibold text-gray-700">Name:</label>
                  <p className="text-lg">{selectedApplicant.name}</p>
                </div>
                <div>
                  <label className="font-semibold text-gray-700">Email:</label>
                  <p>
                    <a href={`mailto:${selectedApplicant.email}`} className="text-blue-600 hover:underline flex items-center gap-2">
                      <Mail size={16} />
                      {selectedApplicant.email}
                    </a>
                  </p>
                </div>
                <div>
                  <label className="font-semibold text-gray-700">CV/Resume:</label>
                  {selectedApplicant.cv_url ? (
                    <p>
                      <a 
                        href={selectedApplicant.cv_url.startsWith('http') ? selectedApplicant.cv_url : `${api.defaults.baseURL}${selectedApplicant.cv_url}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-2"
                      >
                        <ExternalLink size={16} />
                        View CV
                      </a>
                    </p>
                  ) : (
                    <p className="text-gray-500">No CV provided</p>
                  )}
                </div>
                <div>
                  <label className="font-semibold text-gray-700">Cover Letter:</label>
                  {selectedApplicant.cover_letter ? (
                    <p className="bg-gray-50 p-4 rounded border whitespace-pre-wrap">{selectedApplicant.cover_letter}</p>
                  ) : (
                    <p className="text-gray-500">No cover letter provided</p>
                  )}
                </div>
                <div>
                  <label className="font-semibold text-gray-700">Application Status:</label>
                  <p>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      selectedApplicant.status === 'accepted' ? 'bg-green-100 text-green-800' :
                      selectedApplicant.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      selectedApplicant.status === 'interview' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedApplicant.status === 'pending' ? '⏳ Pending' :
                       selectedApplicant.status === 'accepted' ? '✓ Accepted' :
                       selectedApplicant.status === 'interview' ? '📅 Interview Scheduled' :
                       selectedApplicant.status === 'rejected' ? '✗ Rejected' :
                       selectedApplicant.status}
                    </span>
                  </p>
                </div>

                {/* Rating */}
                <div>
                  <label className="font-semibold text-gray-700 block mb-2">Rating:</label>
                  <RatingStars applicationId={selectedApplicant.id} size={24} />
                </div>

                {/* Tags */}
                <div>
                  <label className="font-semibold text-gray-700 block mb-2">Tags:</label>
                  <TagSelector applicationId={selectedApplicant.id} />
                </div>

                {/* Notes Button */}
                <div>
                  <button
                    onClick={() => setShowNotesModal(selectedApplicant.id)}
                    className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center justify-center gap-2"
                  >
                    <StickyNote size={18} />
                    View/Add Notes
                  </button>
                </div>

                <div>
                  <label className="font-semibold text-gray-700">Applied On:</label>
                  <p>{new Date(selectedApplicant.created_at).toLocaleString()}</p>
                </div>
                
                {/* Review Decision Section */}
                {selectedApplicant.status === 'pending' && (
                  <div className="pt-4 border-t bg-gray-50 -mx-6 px-6 py-4">
                    <h4 className="font-semibold text-lg mb-3">Review Decision</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      After reviewing the candidate's qualifications, make your decision:
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          handleUpdateApplicationStatus(selectedApplicant.id, 'accepted');
                          alert('Application accepted! You can now schedule an interview.');
                          setSelectedApplicant(null);
                        }}
                        className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center justify-center gap-2 shadow-lg"
                      >
                        <CheckCircle size={20} />
                        Accept & Proceed to Interview
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to reject this application?')) {
                            handleUpdateApplicationStatus(selectedApplicant.id, 'rejected');
                            setSelectedApplicant(null);
                          }
                        }}
                        className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold shadow-lg"
                      >
                        Reject Application
                      </button>
                    </div>
                  </div>
                )}

                {/* Schedule Interview Section */}
                {selectedApplicant.status === 'accepted' && (
                  <div className="pt-4 border-t bg-green-50 -mx-6 px-6 py-4">
                    <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <CheckCircle className="text-green-600" size={24} />
                      Application Accepted - Next Step
                    </h4>
                    <p className="text-sm text-gray-700 mb-4">
                      This candidate has been accepted. Schedule an interview to continue the hiring process.
                    </p>
                    <button
                      onClick={() => {
                        setShowInterviewScheduler({
                          applicationId: selectedApplicant.id,
                          candidateName: selectedApplicant.name,
                          jobTitle: userJobs.find(j => j.id === selectedApplicant.job_id)?.title || 'Position'
                        });
                        setSelectedApplicant(null);
                      }}
                      className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold flex items-center justify-center gap-2 shadow-lg"
                    >
                      <Calendar size={20} />
                      Schedule Interview Now
                    </button>
                  </div>
                )}

                {/* Interview Scheduled Section */}
                {selectedApplicant.status === 'interview' && (
                  <div className="pt-4 border-t bg-blue-50 -mx-6 px-6 py-4">
                    <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Calendar className="text-blue-600" size={24} />
                      Interview Scheduled - Next Step
                    </h4>
                    <p className="text-sm text-blue-700 mb-4">
                      The interview is scheduled. You can update the outcome after the interview.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          handleUpdateApplicationStatus(selectedApplicant.id, 'hired');
                          setSelectedApplicant(null);
                        }}
                        className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center justify-center gap-2 shadow-lg"
                      >
                        <CheckCircle size={20} />
                        Mark as Hired
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to reject this application?')) {
                            handleUpdateApplicationStatus(selectedApplicant.id, 'rejected');
                            setSelectedApplicant(null);
                          }
                        }}
                        className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold shadow-lg"
                      >
                        Reject Application
                      </button>
                    </div>
                  </div>
                )}

                {/* Rejected Status */}
                {selectedApplicant.status === 'rejected' && (
                  <div className="pt-4 border-t bg-red-50 -mx-6 px-6 py-4">
                    <p className="text-sm text-red-700 flex items-center gap-2">
                      <span className="text-2xl">✗</span>
                      This application has been rejected.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Interview Scheduler Modal */}
        {showInterviewScheduler && (
          <InterviewScheduler
            applicationId={showInterviewScheduler.applicationId}
            candidateName={showInterviewScheduler.candidateName}
            jobTitle={showInterviewScheduler.jobTitle}
            onClose={() => setShowInterviewScheduler(null)}
            onScheduled={() => {
              setShowInterviewScheduler(null);
              // Refresh applications after interview scheduled
              refreshApplications();
              setSelectedApplicant(null);
            }}
          />
        )}

        {/* Notes Modal */}
        {showNotesModal && (
          <NotesModal
            applicationId={showNotesModal}
            onClose={() => setShowNotesModal(null)}
          />
        )}
        {/* Bulk Actions Bar */}
        <BulkActionsBar 
          selectedApplications={selectedApplications}
          onAction={refreshApplications}
          onClearSelection={() => setSelectedApplications([])}
        />

        {/* Candidate Comparison Modal */}
        {showComparison && selectedApplications.length >= 2 && (
          <CandidateComparisonModal
            applicationIds={selectedApplications.slice(0, 5)}
            onClose={() => setShowComparison(false)}
          />
        )}
      </div>
    );
  }

  // OWNER/ADMIN DASHBOARD
  if (user.user_type === 'owner') {
    return (
      <div className="bg-gray-50 min-h-screen py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-4xl font-bold">Platform Admin Dashboard</h1>
            <div className="flex gap-3">
              <a
                href={`${api.defaults.baseURL}/`.replace(/\/+$/, '')}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 font-semibold"
              >
                🗄️ Database Admin
              </a>
              <div className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-semibold">
                Owner/Admin
              </div>
            </div>
          </div>

          {/* Platform Statistics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Total Users */}
            <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-blue-500">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600 font-semibold">Total Users</p>
                <Users className="text-blue-600" size={32} />
              </div>
              <p className="text-4xl font-bold">{stats?.totalUsers || 0}</p>
              <p className="text-sm text-gray-500 mt-2">Platform members</p>
            </div>

            {/* Total Job Seekers */}
            <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-green-500">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600 font-semibold">Job Seekers</p>
                <UserCheck className="text-green-600" size={32} />
              </div>
              <p className="text-4xl font-bold">{stats?.totalJobSeekers || 0}</p>
              <p className="text-sm text-gray-500 mt-2">Active candidates</p>
            </div>

            {/* Total Employers */}
            <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-purple-500">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600 font-semibold">Employers</p>
                <Briefcase className="text-purple-600" size={32} />
              </div>
              <p className="text-4xl font-bold">{stats?.totalEmployers || 0}</p>
              <p className="text-sm text-gray-500 mt-2">Hiring companies</p>
            </div>

            {/* Active Jobs */}
            <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-orange-500">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600 font-semibold">Active Jobs</p>
                <TrendingUp className="text-orange-600" size={32} />
              </div>
              <p className="text-4xl font-bold">{stats?.activeJobs || 0}</p>
              <p className="text-sm text-gray-500 mt-2">Open positions</p>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Total Jobs */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600 font-semibold">Total Jobs</p>
                <FileText className="text-blue-600" size={28} />
              </div>
              <p className="text-3xl font-bold">{stats?.totalJobs || 0}</p>
              <p className="text-sm text-gray-500 mt-2">All time postings</p>
            </div>

            {/* Total Applications */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600 font-semibold">Total Applications</p>
                <BarChart3 className="text-green-600" size={28} />
              </div>
              <p className="text-3xl font-bold">{stats?.totalApplications || 0}</p>
              <p className="text-sm text-gray-500 mt-2">All applications</p>
            </div>

            {/* Accepted Applications */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600 font-semibold">Accepted</p>
                <CheckCircle className="text-green-600" size={28} />
              </div>
              <p className="text-3xl font-bold">{stats?.acceptedApplications || 0}</p>
              <p className="text-sm text-gray-500 mt-2">Successful placements</p>
            </div>
          </div>

          {/* Recent Users */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <Users className="mr-3 text-blue-600" size={28} />
                Recent Users
              </h2>
              {stats?.recentUsers && stats.recentUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left">Name</th>
                        <th className="px-4 py-2 text-left">Type</th>
                        <th className="px-4 py-2 text-left">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentUsers.map((u) => (
                        <tr key={u.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium">{u.name}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              u.user_type === 'job_seeker' ? 'bg-green-100 text-green-800' :
                              u.user_type === 'employer' ? 'bg-blue-100 text-blue-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {u.user_type === 'job_seeker' ? 'Seeker' : u.user_type === 'employer' ? 'Employer' : 'Owner'}
                            </span>
                          </td>
                          <td className="px-4 py-2">{new Date(u.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500">No recent users</p>
              )}
            </div>

            {/* Recent Applications */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <FileText className="mr-3 text-blue-600" size={28} />
                Recent Applications
              </h2>
              {stats?.recentApplications && stats.recentApplications.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left">Candidate</th>
                        <th className="px-4 py-2 text-left">Job</th>
                        <th className="px-4 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentApplications.map((app) => (
                        <tr key={app.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium">{app.applicant_name}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{app.title}</td>
                          <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              app.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {app.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500">No recent applications</p>
              )}
            </div>
          </div>

          {/* Platform Health Section */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6">Platform Health</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-gray-700 mb-4">User Distribution</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Job Seekers</span>
                    <div className="w-48 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-green-500 h-3 rounded-full" 
                        style={{width: `${stats?.totalUsers > 0 ? (stats.totalJobSeekers / stats.totalUsers) * 100 : 0}%`}}
                      ></div>
                    </div>
                    <span className="font-semibold ml-2">{stats?.totalJobSeekers || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Employers</span>
                    <div className="w-48 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-blue-500 h-3 rounded-full" 
                        style={{width: `${stats?.totalUsers > 0 ? (stats.totalEmployers / stats.totalUsers) * 100 : 0}%`}}
                      ></div>
                    </div>
                    <span className="font-semibold ml-2">{stats?.totalEmployers || 0}</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-4">Application Success Rate</h3>
                <div className="flex items-center justify-center py-8">
                  <div className="relative w-32 h-32">
                    <svg className="transform -rotate-90" width="128" height="128">
                      <circle cx="64" cy="64" r="60" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                      <circle 
                        cx="64" 
                        cy="64" 
                        r="60" 
                        fill="none" 
                        stroke="#10b981" 
                        strokeWidth="8"
                        strokeDasharray={`${stats?.totalApplications > 0 ? (stats.acceptedApplications / stats.totalApplications) * 376.99 : 0} 376.99`}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center flex-col">
                      <p className="text-2xl font-bold">{stats?.totalApplications > 0 ? Math.round((stats.acceptedApplications / stats.totalApplications) * 100) : 0}%</p>
                      <p className="text-xs text-gray-500">Success Rate</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User Management Section */}
          <div className="mb-12">
            <UserManagement />
          </div>
        </div>

        {/* Bulk Actions Bar */}
        <BulkActionsBar 
          selectedApplications={selectedApplications}
          onAction={refreshApplications}
          onClearSelection={() => setSelectedApplications([])}
        />

        {/* Candidate Comparison Modal */}
        {showComparison && selectedApplications.length >= 2 && (
          <CandidateComparisonModal
            applicationIds={selectedApplications.slice(0, 5)}
            onClose={() => setShowComparison(false)}
          />
        )}
      </div>
    );
  }

  return null;
}
